import { useState, useEffect } from "react";
import { ApiClient } from "@/services/api";
import { PageHeader, Card, Button, Alert, LoadingSpinner } from "@/components/shared";
import { formatDate } from "@/utils/format";

interface TransactionData {
  id: string;
  type: string;
  amount_cents: number;
  amount_display: string;
  currency: string;
  status: string;
  description: string;
  payment_ref: string | null;
  created_at: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const TYPE_LABELS: Record<string, string> = {
  subscription: "Subscription",
  contact_unlock: "Contact Unlock",
  boost: "Boost",
  upgrade_proration: "Plan Upgrade",
  refund: "Refund",
};

const TYPE_ICONS: Record<string, string> = {
  subscription: "💳",
  contact_unlock: "🔓",
  boost: "🚀",
  upgrade_proration: "⬆️",
  refund: "↩️",
};

const STATUS_COLORS: Record<string, string> = {
  completed: "bg-green-900/40 text-green-400",
  pending: "bg-amber-900/40 text-amber-400",
  failed: "bg-red-900/40 text-red-400",
  refunded: "bg-white/10 text-white/60",
};

const FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "subscription", label: "Subscriptions" },
  { value: "contact_unlock", label: "Unlocks" },
  { value: "boost", label: "Boosts" },
  { value: "upgrade_proration", label: "Upgrades" },
];

export default function BillingHistoryPage() {
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const api = new ApiClient();
        const params = new URLSearchParams({ page: String(page), limit: "20" });
        if (typeFilter) params.set("type", typeFilter);
        const data = await api.get<{ data: TransactionData[]; pagination: PaginationData }>(
          `/transactions/me?${params}`
        );
        setTransactions(data.data);
        setPagination(data.pagination);
      } catch (err: any) {
        // 404 = no agent profile/transactions yet
        if (!err.message?.includes("404")) setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page, typeFilter]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <PageHeader title="Billing History" subtitle="View your payment transactions" />

      {error && <Alert type="error">{error}</Alert>}

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setTypeFilter(opt.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              typeFilter === opt.value
                ? "bg-teal-500 text-white border-teal-500"
                : "bg-midnight text-white/70 border-white/15 hover:border-gold/40"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : transactions.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-white/50">No transactions found</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {transactions.map((t) => (
            <Card key={t.id} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">{TYPE_ICONS[t.type] || "📋"}</span>
                <div>
                  <p className="font-medium text-white text-sm">{t.description || TYPE_LABELS[t.type] || t.type}</p>
                  <p className="text-xs text-white/50">
                    {formatDate(t.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-white">{t.amount_display}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status] || "bg-gray-100 text-gray-800"}`}>
                  {t.status}
                </span>
              </div>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-sm"
              >
                Previous
              </Button>
              <span className="text-sm text-white/50 self-center">
                Page {page} of {pagination.total_pages}
              </span>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.total_pages}
                className="text-sm"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
